import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  AddedPerson,
  AddOwnershipPersonArg,
  DeleteOwnershipPersonArg,
  ExtractionStarted,
  OwnershipChainData,
  OwnershipProgress,
  OwnershipPropertyArg,
  OwnershipRelationshipBody,
  OwnershipRow,
  PreferredDocInfo,
  UpdateOwnershipPersonArg
} from './ownershipTypes';

/**
 * The ownership chain: `/api/lawyer/properties/:id/ownership-chain*` plus the person and
 * relationship writes at `/api/lawyer/ownership-chain/*`. Its own slice because the backend
 * mounts it as its own router ahead of the bare `/api/lawyer` prefix.
 *
 * Normalizers are module-scope, take `unknown` and are TOTAL. The chain is read by a
 * react-flow graph that maps over five arrays at once — one `undefined` there is a crash,
 * so every array defaults to `[]` and every object to `null`.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const envelope = (response: unknown): unknown => {
  const root = asRecord(response);
  return 'data' in root ? root.data : response;
};

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return fallback;
};

const asNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const asRow = (value: unknown): OwnershipRow | null => {
  const raw = asRecord(value);
  return Object.keys(raw).length > 0 ? raw : null;
};

export function normalizeExtractionStarted(response: unknown): ExtractionStarted {
  return { message: asString(asRecord(response).message) };
}

export function normalizeOwnershipProgress(response: unknown): OwnershipProgress {
  const data = asRecord(envelope(response));
  return {
    step: asNumber(data.step),
    stepLabel: asString(data.stepLabel),
    isDone: data.isDone === true,
    hasError: data.hasError === true,
    errorMessage: asNullableString(data.errorMessage)
  };
}

function normalizePreferredDoc(value: unknown): PreferredDocInfo | null {
  const raw = asRecord(value);
  if (Object.keys(raw).length === 0) return null;
  return {
    id: asNumber(raw.id),
    document_type: asString(raw.document_type),
    file_name: asNullableString(raw.file_name),
    uploaded_at: asNullableString(raw.uploaded_at),
    version: asNullableNumber(raw.version),
    status: asString(raw.status)
  };
}

export function normalizeOwnershipChain(response: unknown): OwnershipChainData {
  const data = asRecord(envelope(response));
  return {
    summary: asRow(data.summary),
    persons: asArray<OwnershipRow>(data.persons),
    relationships: asArray<OwnershipRow>(data.relationships),
    transactions: asArray<OwnershipRow>(data.transactions),
    transactionPersons: asArray<OwnershipRow>(data.transactionPersons),
    activeEncumbrances: asArray<OwnershipRow>(data.activeEncumbrances),
    currentOwners: asArray<unknown>(data.currentOwners).map((id) => asNumber(id)),
    isExtracted: data.isExtracted === true,
    isProcessing: data.isProcessing === true,
    extractedAt: asNullableString(data.extractedAt),
    extractedFrom: asNullableString(data.extractedFrom),
    extractedFromDocId: asNullableNumber(data.extractedFromDocId),
    activeEncumbrancesCount: asNumber(data.activeEncumbrancesCount),
    hasEcOrSaleDeed: normalizePreferredDoc(data.hasEcOrSaleDeed)
  };
}

/** `id: 0` is a deterministic sentinel, never an invented id — the caller links a
 * relationship off it, so a `Date.now()` here would write a dangling edge. */
export function normalizeAddedPerson(response: unknown): AddedPerson {
  return { id: asNumber(asRecord(envelope(response)).id) };
}

/** The progress poll and the chain are different reads of the same resource, so the
 * progress cache carries its own SCREAMING-prefixed id under the same tag type. */
const progressTag = (propertyId: number) => `PROGRESS:${String(propertyId)}`;

export const ownershipApi = createApi({
  reducerPath: 'ownershipApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['OwnershipChain'],
  endpoints: (builder) => ({
    getOwnershipChain: builder.query<OwnershipChainData, OwnershipPropertyArg>({
      query: ({ propertyId }) => ({ url: `lawyer/properties/${propertyId}/ownership-chain` }),
      transformResponse: (response: unknown) => normalizeOwnershipChain(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'OwnershipChain', id: String(propertyId) }
      ]
    }),
    getOwnershipProgress: builder.query<OwnershipProgress, OwnershipPropertyArg>({
      query: ({ propertyId }) => ({
        url: `lawyer/properties/${propertyId}/ownership-chain/progress`
      }),
      transformResponse: (response: unknown) => normalizeOwnershipProgress(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'OwnershipChain', id: progressTag(propertyId) }
      ]
    }),
    /** POST with no body, exactly as the axios call sent it — the extractor keys off the url. */
    extractOwnership: builder.mutation<ExtractionStarted, OwnershipPropertyArg>({
      query: ({ propertyId }) => ({
        url: `lawyer/properties/${propertyId}/extract-ownership`,
        method: 'POST'
      }),
      transformResponse: (response: unknown) => normalizeExtractionStarted(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'OwnershipChain', id: String(propertyId) },
        { type: 'OwnershipChain', id: progressTag(propertyId) }
      ]
    }),
    addOwnershipPerson: builder.mutation<AddedPerson, AddOwnershipPersonArg>({
      query: ({ propertyId, body }) => ({
        url: `lawyer/properties/${propertyId}/ownership-chain/person`,
        method: 'POST',
        body
      }),
      transformResponse: (response: unknown) => normalizeAddedPerson(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'OwnershipChain', id: String(propertyId) }
      ]
    }),
    /** The URL keys off `personId`; `propertyId` is carried only so the chain cache for the
     * right property is the one that refetches. */
    updateOwnershipPerson: builder.mutation<void, UpdateOwnershipPersonArg>({
      query: ({ personId, body }) => ({
        url: `lawyer/ownership-chain/${personId}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'OwnershipChain', id: String(propertyId) }
      ]
    }),
    deleteOwnershipPerson: builder.mutation<void, DeleteOwnershipPersonArg>({
      query: ({ personId }) => ({
        url: `lawyer/ownership-chain/${personId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'OwnershipChain', id: String(propertyId) }
      ]
    }),
    addOwnershipRelationship: builder.mutation<void, OwnershipRelationshipBody>({
      query: (body) => ({ url: 'lawyer/ownership-chain/relationship', method: 'POST', body }),
      invalidatesTags: (_result, _error, { property_id }) => [
        { type: 'OwnershipChain', id: String(property_id) }
      ]
    })
  })
});

export const {
  useGetOwnershipChainQuery,
  useGetOwnershipProgressQuery,
  useExtractOwnershipMutation,
  useAddOwnershipPersonMutation,
  useUpdateOwnershipPersonMutation,
  useDeleteOwnershipPersonMutation,
  useAddOwnershipRelationshipMutation
} = ownershipApi;
